"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useMemo } from "react";
import { useTranslations } from "next-intl";
import { useNetlifyForm, netlifyFormMessage } from "@/components/use-netlify-form";
import { FormStatus } from "@/components/form-status";
import { DatePickerField } from "@/components/ui/date-picker";
import { TimePickerField } from "@/components/ui/time-picker";
import rentalDataEn from "@/content/rental-packs-2025-2026.json";
import rentalDataFr from "@/content/rental-packs-2025-2026.fr.json";
import rentalDataNl from "@/content/rental-packs-2025-2026.nl.json";
import rentalDataEs from "@/content/rental-packs-2025-2026.es.json";
import rentalDataIt from "@/content/rental-packs-2025-2026.it.json";
import Image from "next/image";

// Get locale-specific rental data
const getRentalData = (locale: string): typeof rentalDataEn => {
  switch (locale) {
    case 'fr':
      return rentalDataFr as typeof rentalDataEn;
    case 'nl':
      return rentalDataNl as typeof rentalDataEn;
    case 'es':
      return rentalDataEs as typeof rentalDataEn;
    case 'it':
      return rentalDataIt as typeof rentalDataEn;
    default:
      return rentalDataEn;
  }
};
interface ContactFormProps {
  locale: string;
}
interface RentalItem {
  packId: string;
  category: "adult" | "junior" | "other";
  quantity: number;
  duration: "4h" | "1day" | "2days" | "3days" | "4days" | "5days" | "6days" | "7days";
  withInsurance: boolean;
  accessorySize?: "adult" | "junior"; // For accessories that need size specification
  itemType?: "complete" | "skisOnly" | "bootsOnly" | "skisOrSnowboardOnly"; // For separate pricing
  selectedProducts?: string[]; // Selected product IDs from the pack
}
export default function ContactFormWinterRental(t0) {
  const $ = _c(218);
  const {
    locale
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [rentalItems, setRentalItems] = useState(t1);
  const [totalPrice, setTotalPrice] = useState(0);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [carouselIndexes, setCarouselIndexes] = useState(t2);
  const t = useTranslations("ContactWinter");
  const tf = useTranslations("ContactForm");
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setRentalItems([]);
      setTotalPrice(0);
      setCarouselIndexes({});
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    status: netlifyStatus,
    handleSubmit: handleNetlifySubmit
  } = useNetlifyForm(t3);
  const today = new Date().toISOString().split("T")[0];
  let t4;
  if ($[3] !== locale) {
    t4 = getRentalData(locale);
    $[3] = locale;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const rentalData = t4;
  let t5;
  if ($[5] !== rentalData.adultPacks || $[6] !== rentalData.juniorPacks || $[7] !== rentalData.otherRentals) {
    t5 = [...rentalData.adultPacks.map(_temp), ...rentalData.juniorPacks.map(_temp2), ...rentalData.otherRentals.map(_temp3)];
    $[5] = rentalData.adultPacks;
    $[6] = rentalData.juniorPacks;
    $[7] = rentalData.otherRentals;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const allPacks = t5;
  let t6;
  let t7;
  if ($[9] !== allPacks || $[10] !== rentalData.damageInsurance || $[11] !== rentalItems) {
    t6 = () => {
      let total = 0;
      rentalItems.forEach(item => {
        const pack = allPacks.find(p_2 => p_2.id === item.packId && p_2.category === item.category);
        if (pack) {
          let itemPrice = 0;
          if (item.category === "other" && "pricing" in pack) {
            itemPrice = pack.pricing[item.duration] || 0;
          } else {
            if ("pricing" in pack) {
              itemPrice = pack.pricing[item.duration] || 0;
            }
          }
          total = total + itemPrice * item.quantity;
          total;
          if (item.withInsurance && rentalData.damageInsurance) {
            const daysMap = {
              "4h": 1,
              "1day": 1,
              "2days": 2,
              "3days": 3,
              "4days": 4,
              "5days": 5,
              "6days": 6,
              "7days": 7
            };
            const numDays = daysMap[item.duration] || 1;
            total = total + rentalData.damageInsurance.price * numDays * item.quantity;
            total;
          }
        }
      });
      setTotalPrice(total);
    };
    t7 = [rentalItems, allPacks, rentalData.damageInsurance];
    $[9] = allPacks;
    $[10] = rentalData.damageInsurance;
    $[11] = rentalItems;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  if ($[14] !== rentalItems) {
    t8 = index => {
      setRentalItems(rentalItems.filter((_, i) => i !== index));
    };
    $[14] = rentalItems;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const removeRentalItem = t8;
  let t9;
  if ($[16] !== allPacks || $[17] !== rentalItems) {
    t9 = (index_0, field, value) => {
      setRentalItems(rentalItems.map((item_0, i_0) => {
        if (i_0 === index_0) {
          if (field === "category") {
            const defaultPack = allPacks.find(p_3 => p_3.category === value);
            return {
              ...item_0,
              category: value as RentalItem["category"],
              packId: defaultPack?.id || item_0.packId
            };
          }
          return {
            ...item_0,
            [field]: value
          } as RentalItem;
        }
        return item_0;
      }));
    };
    $[16] = allPacks;
    $[17] = rentalItems;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const updateRentalItem = t9;
  let t10;
  if ($[19] !== allPacks) {
    t10 = (packId, category) => {
      const pack_0 = allPacks.find(p_4 => p_4.id === packId && p_4.category === category);
      return pack_0?.name || "";
    };
    $[19] = allPacks;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const getPackName = t10;
  let t11;
  if ($[21] !== allPacks) {
    t11 = item_1 => {
      const pack_1 = allPacks.find(p_5 => p_5.id === item_1.packId && p_5.category === item_1.category);
      if (!pack_1) {
        return 0;
      }
      if ("pricing" in pack_1) {
        if (item_1.itemType && item_1.itemType !== "complete" && "separateItems" in pack_1 && pack_1.separateItems) {
          const separateItems = pack_1.separateItems as Record<string, Record<string, number>>;
          if (item_1.itemType === "skisOnly" && separateItems.skisOnly) {
            return separateItems.skisOnly[item_1.duration] || 0;
          }
          if (item_1.itemType === "bootsOnly" && separateItems.bootsOnly) {
            return separateItems.bootsOnly[item_1.duration] || 0;
          }
          if (item_1.itemType === "skisOrSnowboardOnly" && separateItems.skisOrSnowboardOnly) {
            return separateItems.skisOrSnowboardOnly[item_1.duration] || 0;
          }
        }
        return pack_1.pricing[item_1.duration] || 0;
      }
      return 0;
    };
    $[21] = allPacks;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const getPackPrice = t11;
  let t12;
  if ($[23] !== allPacks) {
    t12 = (packId_0, category_0) => {
      const pack_2 = allPacks.find(p_6 => p_6.id === packId_0 && p_6.category === category_0);
      return pack_2 && "products" in pack_2 ? pack_2.products : [];
    };
    $[23] = allPacks;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const getPackProducts = t12;
  let t13;
  if ($[25] !== tf) {
    t13 = d => {
      if (d === "2h") {
        return "2h";
      }
      if (d === "4h") {
        return "4h";
      }
      if (d === "halfday") {
        return tf("halfDay");
      }
      const n = parseInt(d, 10);
      if (!Number.isNaN(n)) {
        return `${n} ${n === 1 ? tf("day") : tf("days")}`;
      }
      return d;
    };
    $[25] = tf;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const durationLabel = t13;
  let t14;
  if ($[27] !== allPacks || $[28] !== getPackPrice || $[29] !== rentalData.damageInsurance || $[30] !== rentalItems) {
    t14 = () => {
      const itemsData = rentalItems.map((item_2, index_1) => {
        const pack_3 = allPacks.find(p_7 => p_7.id === item_2.packId && p_7.category === item_2.category);
        const packName = pack_3?.name || "Unknown";
        const price = getPackPrice(item_2);
        const daysMap_0 = {
          "4h": 1,
          "1day": 1,
          "2days": 2,
          "3days": 3,
          "4days": 4,
          "5days": 5,
          "6days": 6,
          "7days": 7
        };
        const numDays_0 = daysMap_0[item_2.duration] || 1;
        const insurance = item_2.withInsurance ? rentalData.damageInsurance.price * numDays_0 : 0;
        let itemInfo = `Rider ${index_1 + 1}: ${packName}\n` + `Category: ${item_2.category}\n`;
        if (item_2.itemType && item_2.itemType !== "complete") {
          const typeLabels = {
            skisOnly: "Skis only",
            bootsOnly: "Boots only",
            skisOrSnowboardOnly: "Skis/Snowboard only"
          };
          itemInfo = itemInfo + `Type: ${typeLabels[item_2.itemType] || item_2.itemType}\n`;
        }
        if (item_2.category === "other" && item_2.accessorySize) {
          itemInfo = itemInfo + `Size: ${item_2.accessorySize}\n`;
        }
        if (item_2.selectedProducts && item_2.selectedProducts.length > 0) {
          itemInfo = itemInfo + `Selected products: ${item_2.selectedProducts.join(", ")}\n`;
        }
        itemInfo = itemInfo + (`Quantity: ${item_2.quantity}\n` + `Duration: ${item_2.duration}\n` + `Price per unit: ${price}€\n` + `Insurance: ${item_2.withInsurance ? "Yes (+" + insurance + "\u20AC/unit)" : "No"}\n` + `Subtotal: ${(price + insurance) * item_2.quantity}€`);
        return itemInfo;
      }).join("\n\n");
      return itemsData;
    };
    $[27] = allPacks;
    $[28] = getPackPrice;
    $[29] = rentalData.damageInsurance;
    $[30] = rentalItems;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const prepareFormData = t14;
  let t15;
  let t16;
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <input type="hidden" name="form-name" value="contact-winter-rental" />;
    t16 = <input type="hidden" name="formType" value="rental" />;
    t17 = <input type="hidden" name="season" value="winter" />;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
  } else {
    t15 = $[32];
    t16 = $[33];
    t17 = $[34];
  }
  let t18;
  if ($[35] !== prepareFormData) {
    t18 = prepareFormData();
    $[35] = prepareFormData;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t18) {
    t19 = <input type="hidden" name="rentalData" value={t18} />;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const t20 = `${totalPrice}€`;
  let t21;
  if ($[39] !== t20) {
    t21 = <input type="hidden" name="totalPrice" value={t20} />;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <p className="hidden"><label>Don't fill this out if you're human:{" "}<input name="bot-field" /></label></p>;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t) {
    t23 = t("firstName");
    $[42] = t;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== t23) {
    t24 = <label htmlFor="firstName" className="block text-base font-medium mb-2">{t23} *</label>;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <input type="text" id="firstName" name="firstName" required={true} className="w-full px-4 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background" />;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t24) {
    t26 = <div>{t24}{t25}</div>;
    $[47] = t24;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== t) {
    t27 = t("lastName");
    $[49] = t;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] !== t27) {
    t28 = <label htmlFor="lastName" className="block text-base font-medium mb-2">{t27} *</label>;
    $[51] = t27;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <input type="text" id="lastName" name="lastName" required={true} className="w-full px-4 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background" />;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  let t30;
  if ($[54] !== t28) {
    t30 = <div>{t28}{t29}</div>;
    $[54] = t28;
    $[55] = t30;
  } else {
    t30 = $[55];
  }
  let t31;
  if ($[56] !== t) {
    t31 = t("email");
    $[56] = t;
    $[57] = t31;
  } else {
    t31 = $[57];
  }
  let t32;
  if ($[58] !== t31) {
    t32 = <label htmlFor="email" className="block text-base font-medium mb-2">{t31} *</label>;
    $[58] = t31;
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  let t33;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <input type="email" id="email" name="email" required={true} className="w-full px-4 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background" />;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  if ($[61] !== t32) {
    t34 = <div>{t32}{t33}</div>;
    $[61] = t32;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] !== t) {
    t35 = t("phone");
    $[63] = t;
    $[64] = t35;
  } else {
    t35 = $[64];
  }
  let t36;
  if ($[65] !== t35) {
    t36 = <label htmlFor="phone" className="block text-base font-medium mb-2">{t35}</label>;
    $[65] = t35;
    $[66] = t36;
  } else {
    t36 = $[66];
  }
  let t37;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <input type="tel" id="phone" name="phone" className="w-full px-4 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background" />;
    $[67] = t37;
  } else {
    t37 = $[67];
  }
  let t38;
  if ($[68] !== t36) {
    t38 = <div>{t36}{t37}</div>;
    $[68] = t36;
    $[69] = t38;
  } else {
    t38 = $[69];
  }
  let t39;
  if ($[70] !== tf) {
    t39 = tf("pickupDate");
    $[70] = tf;
    $[71] = t39;
  } else {
    t39 = $[71];
  }
  let t40;
  if ($[72] !== t39) {
    t40 = <label htmlFor="pickupDate" className="text-base font-medium">{t39} *</label>;
    $[72] = t39;
    $[73] = t40;
  } else {
    t40 = $[73];
  }
  let t41;
  if ($[74] !== tf) {
    t41 = tf("dateExample");
    $[74] = tf;
    $[75] = t41;
  } else {
    t41 = $[75];
  }
  let t42;
  if ($[76] !== t41) {
    t42 = <span className="text-sm text-muted-foreground">{t41}</span>;
    $[76] = t41;
    $[77] = t42;
  } else {
    t42 = $[77];
  }
  let t43;
  if ($[78] !== t40 || $[79] !== t42) {
    t43 = <div className="flex items-center justify-between mb-2">{t40}{t42}</div>;
    $[78] = t40;
    $[79] = t42;
    $[80] = t43;
  } else {
    t43 = $[80];
  }
  let t44;
  if ($[81] !== tf) {
    t44 = tf("pickupTime");
    $[81] = tf;
    $[82] = t44;
  } else {
    t44 = $[82];
  }
  let t45;
  if ($[83] !== t44) {
    t45 = <label htmlFor="pickupTime" className="text-base font-medium">{t44}</label>;
    $[83] = t44;
    $[84] = t45;
  } else {
    t45 = $[84];
  }
  let t46;
  if ($[85] !== tf) {
    t46 = tf("timeExample");
    $[85] = tf;
    $[86] = t46;
  } else {
    t46 = $[86];
  }
  let t47;
  if ($[87] !== t46) {
    t47 = <span className="text-sm text-muted-foreground">{t46}</span>;
    $[87] = t46;
    $[88] = t47;
  } else {
    t47 = $[88];
  }
  let t48;
  if ($[89] !== t45 || $[90] !== t47) {
    t48 = <div className="flex items-center justify-between mb-2">{t45}{t47}</div>;
    $[89] = t45;
    $[90] = t47;
    $[91] = t48;
  } else {
    t48 = $[91];
  }
  const t49 = locale === "fr" ? "Choisir une heure" : "Pick a time";
  let t50;
  if ($[92] !== t49) {
    t50 = <TimePickerField name="pickupTime" id="pickupTime" placeholder={t49} />;
    $[92] = t49;
    $[93] = t50;
  } else {
    t50 = $[93];
  }
  let t51;
  if ($[94] !== t48 || $[95] !== t50) {
    t51 = <div>{t48}{t50}</div>;
    $[94] = t48;
    $[95] = t50;
    $[96] = t51;
  } else {
    t51 = $[96];
  }
  let t52;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center"><svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg></div>;
    $[97] = t52;
  } else {
    t52 = $[97];
  }
  let t53;
  if ($[98] !== tf) {
    t53 = tf("couponLabel");
    $[98] = tf;
    $[99] = t53;
  } else {
    t53 = $[99];
  }
  let t54;
  if ($[100] !== t53) {
    t54 = <span className="font-bold text-sm text-foreground">{t53}</span>;
    $[100] = t53;
    $[101] = t54;
  } else {
    t54 = $[101];
  }
  let t55;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <span className="font-bold text-green-600 text-sm">-20%</span>;
    $[102] = t55;
  } else {
    t55 = $[102];
  }
  let t56;
  if ($[103] !== t54) {
    t56 = <div className="flex items-center gap-2 mb-1">{t54}{t55}</div>;
    $[103] = t54;
    $[104] = t56;
  } else {
    t56 = $[104];
  }
  let t57;
  if ($[105] !== tf) {
    t57 = tf("couponLabel");
    $[105] = tf;
    $[106] = t57;
  } else {
    t57 = $[106];
  }
  let t58;
  if ($[107] !== tf) {
    t58 = tf("couponPlaceholder");
    $[107] = tf;
    $[108] = t58;
  } else {
    t58 = $[108];
  }
  let t59;
  if ($[109] !== t57 || $[110] !== t58) {
    t59 = <input type="text" id="couponCode" name="couponCode" aria-label={t57} placeholder={t58} className="w-full px-3 py-2.5 text-base border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-background" />;
    $[109] = t57;
    $[110] = t58;
    $[111] = t59;
  } else {
    t59 = $[111];
  }
  let t60;
  if ($[112] !== t56 || $[113] !== t59) {
    t60 = <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-4 shadow-sm"><div className="flex items-center gap-4">{t52}<div className="flex-1 min-w-0">{t56}{t59}</div></div></div>;
    $[112] = t56;
    $[113] = t59;
    $[114] = t60;
  } else {
    t60 = $[114];
  }
  let t61;
  let t62;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <div className="absolute top-0 right-0 w-32 h-32 bg-green-200 rounded-full -mr-16 -mt-16 opacity-20" />;
    t62 = <div className="absolute bottom-0 left-0 w-24 h-24 bg-teal-200 rounded-full -ml-12 -mb-12 opacity-20" />;
    $[115] = t61;
    $[116] = t62;
  } else {
    t61 = $[115];
    t62 = $[116];
  }
  let t63;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-lg"><svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg></div>;
    $[117] = t63;
  } else {
    t63 = $[117];
  }
  let t64;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = <span>🎿</span>;
    $[118] = t64;
  } else {
    t64 = $[118];
  }
  let t65;
  if ($[119] !== tf) {
    t65 = tf("specialPromotion");
    $[119] = tf;
    $[120] = t65;
  } else {
    t65 = $[120];
  }
  let t66;
  if ($[121] !== t65) {
    t66 = <h4 className="font-bold text-sm text-green-900 mb-1 flex items-center gap-1">{t64}{t65}</h4>;
    $[121] = t65;
    $[122] = t66;
  } else {
    t66 = $[122];
  }
  let t67;
  if ($[123] !== tf) {
    t67 = tf("sevenDayPromo");
    $[123] = tf;
    $[124] = t67;
  } else {
    t67 = $[124];
  }
  let t68;
  if ($[125] !== t67) {
    t68 = <p className="text-sm text-green-800 leading-relaxed">{t67}</p>;
    $[125] = t67;
    $[126] = t68;
  } else {
    t68 = $[126];
  }
  let t69;
  if ($[127] !== t66 || $[128] !== t68) {
    t69 = <div className="relative overflow-hidden bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 border-2 border-green-200 rounded-xl p-4 shadow-md">{t61}{t62}<div className="relative flex items-start gap-3">{t63}<div className="flex-1">{t66}{t68}</div></div></div>;
    $[127] = t66;
    $[128] = t68;
    $[129] = t69;
  } else {
    t69 = $[129];
  }
  let t70;
  if ($[130] !== tf) {
    t70 = tf("rentalEquipment");
    $[130] = tf;
    $[131] = t70;
  } else {
    t70 = $[131];
  }
  let t71;
  if ($[132] !== t70) {
    t71 = <h2 className="text-xl font-bold mb-4">{t70}</h2>;
    $[132] = t70;
    $[133] = t71;
  } else {
    t71 = $[133];
  }
  let t72;
  if ($[134] !== allPacks || $[135] !== rentalItems) {
    t72 = () => {
      const defaultPack_0 = allPacks.find(_temp4);
      if (defaultPack_0) {
        setRentalItems([...rentalItems, {
          packId: defaultPack_0.id,
          category: "adult",
          quantity: 1,
          duration: "1day",
          withInsurance: false
        }]);
      }
    };
    $[134] = allPacks;
    $[135] = rentalItems;
    $[136] = t72;
  } else {
    t72 = $[136];
  }
  let t73;
  if ($[137] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>;
    $[137] = t73;
  } else {
    t73 = $[137];
  }
  let t74;
  if ($[138] !== tf) {
    t74 = tf("addAdult");
    $[138] = tf;
    $[139] = t74;
  } else {
    t74 = $[139];
  }
  let t75;
  if ($[140] !== t72 || $[141] !== t74) {
    t75 = <button type="button" onClick={t72} className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary text-secondary-foreground rounded-lg font-semibold hover:bg-secondary/90 transition-colors text-sm">{t73}{t74}</button>;
    $[140] = t72;
    $[141] = t74;
    $[142] = t75;
  } else {
    t75 = $[142];
  }
  let t76;
  if ($[143] !== allPacks || $[144] !== rentalItems) {
    t76 = () => {
      const defaultPack_1 = allPacks.find(_temp5);
      if (defaultPack_1) {
        setRentalItems([...rentalItems, {
          packId: defaultPack_1.id,
          category: "junior",
          quantity: 1,
          duration: "1day",
          withInsurance: false
        }]);
      }
    };
    $[143] = allPacks;
    $[144] = rentalItems;
    $[145] = t76;
  } else {
    t76 = $[145];
  }
  let t77;
  if ($[146] === Symbol.for("react.memo_cache_sentinel")) {
    t77 = <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>;
    $[146] = t77;
  } else {
    t77 = $[146];
  }
  let t78;
  if ($[147] !== tf) {
    t78 = tf("addJunior");
    $[147] = tf;
    $[148] = t78;
  } else {
    t78 = $[148];
  }
  let t79;
  if ($[149] !== t76 || $[150] !== t78) {
    t79 = <button type="button" onClick={t76} className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary text-secondary-foreground rounded-lg font-semibold hover:bg-secondary/90 transition-colors text-sm">{t77}{t78}</button>;
    $[149] = t76;
    $[150] = t78;
    $[151] = t79;
  } else {
    t79 = $[151];
  }
  let t80;
  if ($[152] !== allPacks || $[153] !== rentalItems) {
    t80 = () => {
      const defaultPack_2 = allPacks.find(_temp6);
      if (defaultPack_2) {
        setRentalItems([...rentalItems, {
          packId: defaultPack_2.id,
          category: "other",
          quantity: 1,
          duration: "1day",
          withInsurance: false,
          accessorySize: "adult"
        }]);
      }
    };
    $[152] = allPacks;
    $[153] = rentalItems;
    $[154] = t80;
  } else {
    t80 = $[154];
  }
  let t81;
  if ($[155] === Symbol.for("react.memo_cache_sentinel")) {
    t81 = <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>;
    $[155] = t81;
  } else {
    t81 = $[155];
  }
  let t82;
  if ($[156] !== tf) {
    t82 = tf("addAccessory");
    $[156] = tf;
    $[157] = t82;
  } else {
    t82 = $[157];
  }
  let t83;
  if ($[158] !== t80 || $[159] !== t82) {
    t83 = <button type="button" onClick={t80} className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent/90 transition-colors text-sm">{t81}{t82}</button>;
    $[158] = t80;
    $[159] = t82;
    $[160] = t83;
  } else {
    t83 = $[160];
  }
  let t84;
  if ($[161] !== t75 || $[162] !== t79 || $[163] !== t83) {
    t84 = <div className="flex flex-wrap gap-2 mb-4">{t75}{t79}{t83}</div>;
    $[161] = t75;
    $[162] = t79;
    $[163] = t83;
    $[164] = t84;
  } else {
    t84 = $[164];
  }
  let t85;
  if ($[165] !== rentalItems.length || $[166] !== tf) {
    t85 = rentalItems.length === 0 && <div className="p-8 text-center bg-muted/30 rounded-lg"><p className="text-muted-foreground">{tf("emptyRentalMessage")}</p></div>;
    $[165] = rentalItems.length;
    $[166] = tf;
    $[167] = t85;
  } else {
    t85 = $[167];
  }
  let t86;
  if ($[168] !== allPacks || $[169] !== carouselIndexes || $[170] !== getPackProducts || $[171] !== removeRentalItem || $[172] !== rentalData.damageInsurance || $[173] !== rentalItems || $[174] !== tf || $[175] !== updateRentalItem) {
    t86 = rentalItems.map((item_3, index_2) => {
      const pack_4 = allPacks.find(p_11 => p_11.id === item_3.packId && p_11.category === item_3.category);
      const products = getPackProducts(item_3.packId, item_3.category);
      return <div key={index_2} className="mb-6 p-5 bg-card rounded-xl relative border border-border shadow-sm"><button type="button" onClick={() => removeRentalItem(index_2)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-muted hover:bg-destructive/20 flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors" aria-label="Remove item">✕</button><div className="flex items-center gap-3 mb-4 pb-3 border-b"><div className="w-8 h-8 rounded-full bg-[url('/leather.jpg')] bg-cover bg-center border border-[#2a1f10]/50 flex items-center justify-center text-white"><svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="4" r="2" /><path d="M6.5 6.5L12 12l6 6" /><path d="M8 21l2-7 4 3" /><path d="M17 11l-2 6" /></svg></div><h4 className="font-bold text-lg">{item_3.category === "other" ? tf("accessories") : `${tf("skier")} ${rentalItems.slice(0, index_2 + 1).filter(_temp7).length}`}</h4></div><div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4"><div><label htmlFor={`category-${index_2}`} className="block text-base font-medium mb-2">{tf("category")}</label><select id={`category-${index_2}`} value={item_3.category} onChange={e => updateRentalItem(index_2, "category", e.target.value)} className="w-full pl-4 pr-10 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3cpath%20fill%3D%22%236b7280%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2000-1.414-1.414z%22%2F%3E%3c%2Fsvg%3E')] bg-[length:12px] bg-[right_12px_center] bg-no-repeat"><option value="adult">{tf("adult")}</option><option value="junior">{tf("junior")}</option><option value="other">{tf("accessories")}</option></select></div><div><label htmlFor={`pack-${index_2}`} className="block text-base font-medium mb-2">{tf("packItem")}</label><select id={`pack-${index_2}`} value={item_3.packId} onChange={e_0 => updateRentalItem(index_2, "packId", e_0.target.value)} className="w-full pl-4 pr-10 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3cpath%20fill%3D%22%236b7280%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2000-1.414-1.414z%22%2F%3E%3c%2Fsvg%3E')] bg-[length:12px] bg-[right_12px_center] bg-no-repeat">{allPacks.filter(p_12 => p_12.category === item_3.category).map(_temp8)}</select></div><div><label htmlFor={`duration-${index_2}`} className="block text-base font-medium mb-2">{tf("duration")}</label><select id={`duration-${index_2}`} value={item_3.duration} onChange={e_1 => updateRentalItem(index_2, "duration", e_1.target.value)} className="w-full pl-4 pr-10 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3cpath%20fill%3D%22%236b7280%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2000-1.414-1.414z%22%2F%3E%3c%2Fsvg%3E')] bg-[length:12px] bg-[right_12px_center] bg-no-repeat"><option value="4h">4h</option><option value="1day">1 {tf("day")}</option><option value="2days">2 {tf("days")}</option><option value="3days">3 {tf("days")}</option><option value="4days">4 {tf("days")}</option><option value="5days">5 {tf("days")}</option><option value="6days">6 {tf("days")}</option><option value="7days">7 {tf("days")} ⭐</option></select></div></div>{item_3.category !== "other" && pack_4 && "separateItems" in pack_4 && pack_4.separateItems && <div className="mb-4"><label htmlFor={`itemType-${index_2}`} className="block text-base font-medium mb-2">{tf("rentalType")}</label><select id={`itemType-${index_2}`} value={item_3.itemType || "complete"} onChange={e_2 => updateRentalItem(index_2, "itemType", e_2.target.value)} className="w-full pl-4 pr-10 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3cpath%20fill%3D%22%236b7280%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2000-1.414-1.414z%22%2F%3E%3c%2Fsvg%3E')] bg-[length:12px] bg-[right_12px_center] bg-no-repeat"><option value="complete">{tf("completePackEquipmentBoots")}</option>{(pack_4.separateItems as Record<string, Record<string, number>>).skisOnly && <option value="skisOnly">{tf("skisOnly")}</option>}{(pack_4.separateItems as Record<string, Record<string, number>>).skisOrSnowboardOnly && <option value="skisOrSnowboardOnly">{tf("skisSnowboardOnly")}</option>}{(pack_4.separateItems as Record<string, Record<string, number>>).bootsOnly && <option value="bootsOnly">{tf("bootsOnly")}</option>}</select></div>}{item_3.category === "other" && <div className="mb-4"><label htmlFor={`size-${index_2}`} className="block text-base font-medium mb-2">{tf("size")}</label><select id={`size-${index_2}`} value={item_3.accessorySize || "adult"} onChange={e_3 => updateRentalItem(index_2, "accessorySize", e_3.target.value)} className="w-full pl-4 pr-10 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3cpath%20fill%3D%22%236b7280%22%20d%3D%22M10.293%203.293L6%207.586%201.707%203.293A1%201%200%2000.293%204.707l5%205a1%201%200%20001.414%200l5-5a1%201%200%2000-1.414-1.414z%22%2F%3E%3c%2Fsvg%3E')] bg-[length:12px] bg-[right_12px_center] bg-no-repeat"><option value="adult">{tf("adult")}</option><option value="junior">{tf("juniorChild")}</option></select></div>}<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4"><div><label htmlFor={`quantity-${index_2}`} className="block text-base font-medium mb-2">{tf("quantity")}</label><input type="number" id={`quantity-${index_2}`} min="1" value={item_3.quantity} onChange={e_4 => updateRentalItem(index_2, "quantity", parseInt(e_4.target.value) || 1)} className="w-full px-4 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background" /></div><div><p className="block text-base font-medium mb-2">{tf("insurance")}</p><label className="flex items-center gap-2 cursor-pointer px-4 py-3 border rounded-lg bg-background"><input type="checkbox" checked={item_3.withInsurance} onChange={e_5 => updateRentalItem(index_2, "withInsurance", e_5.target.checked)} className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary" /><span className="text-base">{tf("damage")} (+{rentalData.damageInsurance.price}€/{tf("day")})</span></label></div></div>{products.length > 0 && <div className="mt-4 p-4 bg-background rounded-lg border"><p className="text-base font-semibold mb-2">{pack_4?.name || tf("selectedPack")}</p><p className="text-sm text-muted-foreground mb-4">{tf("clickToSelectProducts")}</p><div className="relative"><div className="grid grid-cols-1 sm:grid-cols-3 gap-3">{(() => {
                const currentSlideIndex = carouselIndexes[index_2] || 0;
                const startIdx = currentSlideIndex * 3;
                const slideProducts = products.slice(startIdx, startIdx + 3);
                const selectedProducts = item_3.selectedProducts || [];
                return slideProducts.map((product, pIdx) => {
                  const productId = `${product.name}-${startIdx + pIdx}`;
                  const isSelected = selectedProducts.includes(productId);
                  return <button key={pIdx} type="button" onClick={() => {
                    const currentSelected = item_3.selectedProducts || [];
                    const newSelected = isSelected ? currentSelected.filter(id => id !== productId) : [...currentSelected, productId];
                    updateRentalItem(index_2, "selectedProducts", newSelected);
                  }} className={`text-left bg-muted/30 rounded-lg overflow-hidden transition-all ${isSelected ? "ring-2 ring-primary ring-offset-2" : "hover:bg-muted/50"}`}><div className="relative aspect-video"><Image src={product.photo} alt={product.name} fill={true} className="object-cover" sizes="(max-width: 640px) 100vw, 33vw" />{isSelected && <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center"><svg className="w-4 h-4 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg></div>}</div><div className="p-3"><p className="font-semibold text-sm text-foreground mb-1">{product.name}</p>{product.description && <p className="text-xs text-muted-foreground line-clamp-2">{product.description}</p>}</div></button>;
                });
              })()}</div>{products.length > 3 && <><button type="button" onClick={() => {
                const currentSlide = carouselIndexes[index_2] || 0;
                const totalSlides = Math.ceil(products.length / 3);
                const newSlide = currentSlide === 0 ? totalSlides - 1 : currentSlide - 1;
                setCarouselIndexes({
                  ...carouselIndexes,
                  [index_2]: newSlide
                });
              }} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors text-xl" aria-label="Previous">‹</button><button type="button" onClick={() => {
                const currentSlide_0 = carouselIndexes[index_2] || 0;
                const totalSlides_0 = Math.ceil(products.length / 3);
                const newSlide_0 = currentSlide_0 === totalSlides_0 - 1 ? 0 : currentSlide_0 + 1;
                setCarouselIndexes({
                  ...carouselIndexes,
                  [index_2]: newSlide_0
                });
              }} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors text-xl" aria-label="Next">›</button></>}{products.length > 3 && <div className="flex justify-center gap-1.5 mt-4">{Array.from({
                length: Math.ceil(products.length / 3)
              }).map((__0, slideIdx) => <button key={slideIdx} type="button" onClick={() => setCarouselIndexes({
                ...carouselIndexes,
                [index_2]: slideIdx
              })} className={`w-2 h-2 rounded-full transition-colors ${(carouselIndexes[index_2] || 0) === slideIdx ? "bg-primary" : "bg-muted-foreground/30 hover:bg-muted-foreground/50"}`} aria-label={`Go to slide ${slideIdx + 1}`} />)}</div>}</div></div>}</div>;
    });
    $[168] = allPacks;
    $[169] = carouselIndexes;
    $[170] = getPackProducts;
    $[171] = removeRentalItem;
    $[172] = rentalData.damageInsurance;
    $[173] = rentalItems;
    $[174] = tf;
    $[175] = updateRentalItem;
    $[176] = t86;
  } else {
    t86 = $[176];
  }
  let t87;
  if ($[177] !== t71 || $[178] !== t84 || $[179] !== t85 || $[180] !== t86) {
    t87 = <div>{t71}{t84}{t85}{t86}</div>;
    $[177] = t71;
    $[178] = t84;
    $[179] = t85;
    $[180] = t86;
    $[181] = t87;
  } else {
    t87 = $[181];
  }
  let t88;
  if ($[182] !== t) {
    t88 = t("message");
    $[182] = t;
    $[183] = t88;
  } else {
    t88 = $[183];
  }
  let t89;
  if ($[184] !== t88) {
    t89 = <label htmlFor="message" className="block text-base font-medium mb-2">{t88}</label>;
    $[184] = t88;
    $[185] = t89;
  } else {
    t89 = $[185];
  }
  let t90;
  if ($[186] !== tf) {
    t90 = tf("messagePlaceholderWinter");
    $[186] = tf;
    $[187] = t90;
  } else {
    t90 = $[187];
  }
  let t91;
  if ($[188] !== t90) {
    t91 = <textarea id="message" name="message" rows={3} placeholder={t90} className="w-full px-4 py-3 text-base border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background" />;
    $[188] = t90;
    $[189] = t91;
  } else {
    t91 = $[189];
  }
  let t92;
  if ($[190] !== t89 || $[191] !== t91) {
    t92 = <div>{t89}{t91}</div>;
    $[190] = t89;
    $[191] = t91;
    $[192] = t92;
  } else {
    t92 = $[192];
  }
  let t93;
  if ($[193] !== durationLabel || $[194] !== getPackName || $[195] !== getPackPrice || $[196] !== rentalData.damageInsurance || $[197] !== rentalItems || $[198] !== tf || $[199] !== totalPrice) {
    t93 = rentalItems.length > 0 && <div className="bg-muted/30 border rounded-xl p-6"><div className="flex justify-between items-center mb-5 pb-4 border-b"><h2 className="text-2xl font-bold text-foreground">{tf("summary")}</h2><div className="text-3xl font-bold text-foreground">{totalPrice}€</div></div><div className="space-y-3"><div className="text-base font-bold text-foreground mb-3">{tf("selectedItems")}</div>{rentalItems.map((item_4, index_3) => {
          const itemPrice_0 = getPackPrice(item_4);
          const daysMap_1 = {
            "4h": 1,
            "1day": 1,
            "2days": 2,
            "3days": 3,
            "4days": 4,
            "5days": 5,
            "6days": 6,
            "7days": 7
          };
          const numDays_1 = daysMap_1[item_4.duration] || 1;
          const insurancePrice = item_4.withInsurance ? rentalData.damageInsurance.price * numDays_1 : 0;
          return <div key={index_3} className="bg-background border rounded-lg p-4"><div className="flex justify-between items-start mb-2"><div className="font-bold text-base text-foreground">{getPackName(item_4.packId, item_4.category)}<span className="text-muted-foreground font-medium ml-1">×{item_4.quantity}</span></div><span className="font-bold text-base text-foreground">{(itemPrice_0 + insurancePrice) * item_4.quantity}€</span></div><div className="flex gap-2 text-sm font-medium text-muted-foreground"><span>{durationLabel(item_4.duration)}</span>{item_4.withInsurance && <><span>•</span><span>{tf("withInsurance")}</span></>}{item_4.duration === "7days" && <span>🎉</span>}</div></div>;
        })}</div></div>;
    $[193] = durationLabel;
    $[194] = getPackName;
    $[195] = getPackPrice;
    $[196] = rentalData.damageInsurance;
    $[197] = rentalItems;
    $[198] = tf;
    $[199] = totalPrice;
    $[200] = t93;
  } else {
    t93 = $[200];
  }
  const t94 = netlifyStatus === "submitting";
  let t95;
  if ($[201] !== locale || $[202] !== netlifyStatus || $[203] !== t) {
    t95 = netlifyStatus === "submitting" ? netlifyFormMessage(locale, "submitting") : t("submitButton");
    $[201] = locale;
    $[202] = netlifyStatus;
    $[203] = t;
    $[204] = t95;
  } else {
    t95 = $[204];
  }
  let t96;
  if ($[205] !== t94 || $[206] !== t95) {
    t96 = <button type="submit" disabled={t94} className="w-full bg-primary text-primary-foreground px-6 py-4 text-lg rounded-lg font-semibold hover:bg-primary/90 transition-colors shadow-md hover:shadow-lg disabled:opacity-60">{t95}</button>;
    $[205] = t94;
    $[206] = t95;
    $[207] = t96;
  } else {
    t96 = $[207];
  }
  let t97;
  if ($[208] !== locale || $[209] !== netlifyStatus) {
    t97 = <FormStatus status={netlifyStatus} locale={locale} />;
    $[208] = locale;
    $[209] = netlifyStatus;
    $[210] = t97;
  } else {
    t97 = $[210];
  }
  let t98;
  if ($[211] !== t69 || $[212] !== t87 || $[213] !== t92 || $[214] !== t93 || $[215] !== t96 || $[216] !== t97) {
    t98 = <div className="lg:col-span-8 space-y-6">{t69}{t87}{t92}{t93}{t96}{t97}</div>;
    $[211] = t69;
    $[212] = t87;
    $[213] = t92;
    $[214] = t93;
    $[215] = t96;
    $[216] = t97;
    $[217] = t98;
  } else {
    t98 = $[217];
  }
  return <div className="bg-card rounded-lg border p-4 sm:p-6 lg:p-8 w-full max-w-[1440px] mx-auto"><form name="contact-winter-rental" method="POST" data-netlify="true" netlify-honeypot="bot-field" onSubmit={handleNetlifySubmit} className="space-y-6">{t15}{t16}{t17}{t19}{t21}{t22}<div className="grid grid-cols-1 lg:grid-cols-12 gap-6"><div className="lg:col-span-4 space-y-4 lg:p-6 lg:bg-muted/30 lg:rounded-lg">{t26}{t30}{t34}{t38}<div>{t43}<DatePickerField name="pickupDate" id="pickupDate" locale={locale} required={true} minDate={new Date(today)} placeholder={locale === "fr" ? "Choisir une date" : "Pick a date"} /></div>{t51}{t60}</div>{t98}</div></form></div>;
}
function _temp8(pack_5) {
  return <option key={pack_5.id} value={pack_5.id}>{pack_5.name}</option>;
}
function _temp7(it) {
  return it.category !== "other";
}
function _temp6(p_10) {
  return p_10.category === "other";
}
function _temp5(p_9) {
  return p_9.category === "junior";
}
function _temp4(p_8) {
  return p_8.category === "adult";
}
function _temp3(p_1) {
  return {
    ...p_1,
    category: "other" as const
  };
}
function _temp2(p_0) {
  return {
    ...p_0,
    category: "junior" as const
  };
}
function _temp(p) {
  return {
    ...p,
    category: "adult" as const
  };
}